import React, { useState, useEffect } from "react";
import {
  ThinTextField,
  ThinDropdownWithSearch,
} from "../common/GeneralComponent";
import { toast } from "react-toastify";
import { fetchData } from "../../utils/functions";
import Select from "react-select";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom"

const LearningFacilities = (props) => {
  
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, schoolProfileId, onUpdate } = props;
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDqaApproved, setIsDqaApproved] = useState(false);
  const [isDqaRejected, setIsDqaRejected] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [loader, setLoader] = useState(false);

  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    library: "",
    books: "",
    computer_lab: "",
    computers: "",
    science_lab: "",
    science_lab_equipped: "",
    playground: "",
    surface: "",
    hall: "",
    supplementary: "",
    learning_materials_others: "",
    extracurricular: [],
    extracurricular_activities: "",
  });


  
     const fetchSchoolAssessment = async () => {
        try {
          setLoader(true);
          setIsFetching(true);
    
          const response = await fetchData(
            `school-fetchById/${id}`,
            "GET",
            null,
            {},
            {},
            navigate
          );
    
          if (response?.data) {
            // setSchoolData(response.data);
    
            // Check for DQA approval at root level
            const isDqaApproved =
              response.data?.SchoolProfile.verified_from_dqa === 1 ||
              response.data?.SchoolProfile.verified_from_dqa === "1";

              const isDqaRejected = 
                response.data?.SchoolProfile.verified_from_dqa === 2 ||
                response.data?.SchoolProfile.verified_from_dqa === "2";
    
            setIsDqaApproved(isDqaApproved);
            setIsDqaRejected(isDqaRejected);
          }
        } catch (error) {
          console.error("Error fetching school data:", error);
          toast.error("Failed to load school data.");
        } finally {
          setLoader(false);
          setIsFetching(false);
        }
      };
    
      useEffect(() => {
        fetchSchoolAssessment();
      }, [id]);
      
  //Yup schema for validation

  const validationSchema = Yup.object().shape({
    library: Yup.string().required("Library Room is required"),

    books: Yup.number()
      .transform((value, originalValue) =>
        String(originalValue).trim() === "" ? null : value
      )
      .nullable()
      .min(0, "Value cannot be negative")
      .when("library", {
        is: "Yes",
        then: (schema) =>
          schema.required("Number of Books is required when Library is Yes"),
        otherwise: (schema) => schema.notRequired(),
      }),

    computer_lab: Yup.string().required("Computer Lab is required"),

    computers: Yup.number()
      .transform((value, originalValue) =>
        String(originalValue).trim() === "" ? null : value
      )
      .nullable()
      .min(0, "Value cannot be negative")
      .when("computer_lab", {
        is: "Yes",
        then: (schema) =>
          schema.required(
            "Number of Computers is required when Computer Lab is Yes"
          ),
        otherwise: (schema) => schema.notRequired(),
      }),

    science_lab: Yup.string().required("Science Lab is required"),

    science_lab_equipped: Yup.string().when("science_lab", {
      is: "Yes",
      then: (schema) =>
        schema.required(
          "Science Lab Equipment status is required when Science Lab is Yes"
        ),
      otherwise: (schema) => schema.notRequired(),
    }),

    playground: Yup.string().required("Playground is required"),

    surface: Yup.string().required("Surface is required"),

    hall: Yup.string().required("Hall or Assembly Area is required"),

    supplementary: Yup.string().required(
      "Supplementary Learning Materials is required"
    ),
    learning_materials_others: Yup.string().when("supplementary", {
      is: "Others",
      then: (schema) =>
        schema
          .required("Supplementary Learning Materials (Others) is required")
          .min(3, "Please enter at least 3 characters."),
      otherwise: (schema) => schema.notRequired(),
    }),
    extracurricular: Yup.array()
      .min(1, "Select at least one extracurricular activity")
      .required("Extracurricular Activities is required"),
    extracurricular_activities: Yup.string().required(
      "Extra-curricular Activities is required"
    ),
  });

  const booleanToYesNo = (value) => {
    if (value === true) return "Yes";
    if (value === false) return "No";
    return "";
  };

  const extracurricularOptions = [
    { label: "Drama", value: "Drama" },
    { label: "Debate", value: "Debate" },
    { label: "Games", value: "Games" },
    { label: "Sports", value: "Sports" },
    { label: "Music", value: "Music" },
    { label: "Other", value: "Other" },
  ];

  useEffect(() => {
    if (data) {
      setFormData({
        library: booleanToYesNo(data.library_room),
        books: String(data.library_books_count ?? ""),
        computer_lab: booleanToYesNo(data.computer_lab),
        computers: String(data.computers_count ?? ""),
        science_lab: booleanToYesNo(data.science_lab),
        science_lab_equipped: booleanToYesNo(data.science_lab_equipped),
        playground: data.playground_location || "",
        surface: data.playground_surface || "",
        hall: booleanToYesNo(data.hall_assembly_area),
        supplementary: data.learning_materials || "",
        learning_materials_others: data.learning_materials_others || "",
        extracurricular: data.extracurricular_types
          ? data.extracurricular_types.split(",")
          : [],
        extracurricular_activities: booleanToYesNo(
          data.extracurricular_activities
        ),
      });
    }
  }, [data]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when field is updated
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleNumericChange = (field, value) => {
    if (value === "" || /^\d+$/.test(value)) {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Clear error when field is updated
      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    }
  };

  const handleUpdate = async () => {
    setIsUpdating(true);
    setErrors({});

    try {
      await validationSchema.validate(formData, { abortEarly: false });

      const profileId = schoolProfileId || data?.school_profile_id;

      if (!profileId) {
        toast.error("School profile ID not found");
        return;
      }

      const updatePayload = {
        library_room: formData.library === "Yes",
        library_books_count: parseInt(formData.books) || 0,
        computer_lab: formData.computer_lab === "Yes",
        computers_count: parseInt(formData.computers) || 0,
        science_lab: formData.science_lab === "Yes",
        science_lab_equipped: formData.science_lab_equipped === "Yes",
        playground_location: formData.playground,
        playground_surface: formData.surface,
        hall_assembly_area: formData.hall === "Yes",
        learning_materials: formData.supplementary,
        learning_materials_others:
          formData.supplementary === "Others"
            ? formData.learning_materials_others
            : null,
        // extracurricular_activities: formData.extracurricular.length > 0,
        extracurricular_types: formData.extracurricular.join(","),
        extracurricular_activities:
          formData.extracurricular_activities === "Yes",
        extracurricular_types: formData.extracurricular.join(","),
      };

      const response = await fetchData(
        `school-combined-update/${profileId}`,
        "PUT",
        {
          school_learning_facilities: JSON.stringify(updatePayload),
          school_profile: "{}",
          school_infrastructure: "{}",
          school_administrative_details: "{}",
          school_additional_notes: "{}",
        },
        { "Content-Type": "application/json" }
      );

      if (response.success) {
        toast.success("Learning facilities data updated successfully!");
        if (onUpdate) onUpdate();
      } else {
        toast.error(
          response.message || "Failed to update learning facilities data"
        );
      }
    } catch (error) {
      if (error.inner) {
        const newErrors = {};
        error.inner.forEach((err) => {
          newErrors[err.path] = err.message;
        });
        setErrors(newErrors);
      } else {
        toast.error("Error updating learning facilities data");
        console.error("Update error:", error);
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const playgroundOptions = [
    { label: "Inside", value: "Inside" },
    { label: "Nearby", value: "Nearby" },
    { label: "None", value: "None" },
  ];

  const surfaceOptions = [
    { label: "Leveled", value: "Leveled" },
    { label: "Uneven", value: "Uneven" },
  ];

  const supplementaryOptions = [
    { label: "Charts", value: "Charts" },
    { label: "Models", value: "Models" },
    { label: "None", value: "None" },
    { label: "Others", value: "Others" },
  ];

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        {isDqaApproved && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Learning Facilities
            </h2>
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          </>
        )}
        {isDqaRejected && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Learning Facilities
            </h2>
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
              Rejected from DQA
            </span>
          </>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Library */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Library Room <span className="text-red-500 text-xs"> * </span>
          </label>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-1">
              <input disabled={isDqaApproved || isDqaRejected}
                type="radio"
                name="library"
                value="Yes"
                checked={formData.library === "Yes"}
                onChange={() => handleChange("library", "Yes")}
                className="accent-blue-600"
              />
              <span className="text-sm text-gray-700">Yes</span>
            </label>
            <label className="flex items-center gap-1">
              <input disabled={isDqaApproved || isDqaRejected}
                type="radio"
                name="library"
                value="No"
                checked={formData.library === "No"}
                onChange={() => handleChange("library", "No")}
                className="accent-blue-600"
              />
              <span className="text-sm text-gray-700">No</span>
            </label>
          </div>
          {errors.library && (
            <p className="text-red-500 text-xs">{errors.library}</p>
          )}
          {formData.library === "Yes" && (
            <div className="mt-2">
              <ThinTextField disabled={isDqaApproved || isDqaRejected}
                label="No. of Books "
                value={formData.books}
                onChange={(e) => handleNumericChange("books", e.target.value)}
              />
              {errors.books && (
                <p className="text-red-500 text-xs">{errors.books}</p>
              )}
            </div>
          )}
        </div>

        {/* Computer Lab */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Computer Lab <span className="text-red-500 text-xs"> * </span>
          </label>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-1">
              <input disabled={isDqaApproved || isDqaRejected}
                type="radio"
                name="computer_lab"
                value="Yes"
                checked={formData.computer_lab === "Yes"}
                onChange={() => handleChange("computer_lab", "Yes")}
                className="accent-blue-600"
              />
              <span className="text-sm text-gray-700">Yes</span>
            </label>
            <label className="flex items-center gap-1">
              <input disabled={isDqaApproved || isDqaRejected}
                type="radio"
                name="computer_lab"
                value="No"
                checked={formData.computer_lab === "No"}
                onChange={() => handleChange("computer_lab", "No")}
                className="accent-blue-600"
              />
              <span className="text-sm text-gray-700">No</span>
            </label>
          </div>
          {errors.computer_lab && (
            <p className="text-red-500 text-xs">{errors.computer_lab}</p>
          )}
          {formData.computer_lab === "Yes" && (
            <div className="mt-2">
              <ThinTextField disabled={isDqaApproved || isDqaRejected}
                label="No. of Computers"
                value={formData.computers}
                onChange={(e) =>
                  handleNumericChange("computers", e.target.value)
                }
              />
              {errors.computers && (
                <p className="text-red-500 text-xs">{errors.computers}</p>
              )}
            </div>
          )}
        </div>

        {/* Science Lab */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Science Lab <span className="text-red-500 text-xs"> * </span>
          </label>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-1">
              <input disabled={isDqaApproved || isDqaRejected}
                type="radio"
                name="science_lab"
                value="Yes"
                checked={formData.science_lab === "Yes"}
                onChange={() => handleChange("science_lab", "Yes")}
                className="accent-blue-600"
              />
              <span className="text-sm text-gray-700">Yes</span>
            </label>
            <label className="flex items-center gap-1">
              <input disabled={isDqaApproved || isDqaRejected}
                type="radio"
                name="science_lab"
                value="No"
                checked={formData.science_lab === "No"}
                onChange={() => handleChange("science_lab", "No")}
                className="accent-blue-600"
              />
              <span className="text-sm text-gray-700">No</span>
            </label>
          </div>
          {errors.science_lab && (
            <p className="text-red-500 text-xs">{errors.science_lab}</p>
          )}
          {formData.science_lab === "Yes" && (
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 ">
                Well-equipped <span className="text-red-500 text-xs"> * </span>{" "}
              </label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1">
                  <input disabled={isDqaApproved || isDqaRejected}
                    type="radio"
                    name="science_lab_equipped"
                    value="Yes"
                    checked={formData.science_lab_equipped === "Yes"}
                    onChange={() => handleChange("science_lab_equipped", "Yes")}
                    className="accent-blue-600"
                  />
                  <span className="text-sm text-gray-700">Yes</span>
                </label>
                <label className="flex items-center gap-1">
                  <input disabled={isDqaApproved || isDqaRejected}
                    type="radio"
                    name="science_lab_equipped"
                    value="No"
                    checked={formData.science_lab_equipped === "No"}
                    onChange={() => handleChange("science_lab_equipped", "No")}
                    className="accent-blue-600"
                  />
                  <span className="text-sm text-gray-700">No</span>
                </label>
              </div>
              {errors.science_lab_equipped && (
                <p className="text-red-500 text-xs">
                  {errors.science_lab_equipped}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Playground */}
        <div>
          <ThinDropdownWithSearch disabled={isDqaApproved || isDqaRejected}
            label="Playground "
            options={playgroundOptions}
            value={formData.playground}
            onChange={(val) => handleChange("playground", val)}
          />
          {errors.playground && (
            <p className="text-red-500 text-xs">{errors.playground}</p>
          )}
        </div>

        {/* Surface */}
        <div>
          <ThinDropdownWithSearch disabled={isDqaApproved || isDqaRejected}
            label="Surface "
            options={surfaceOptions}
            value={formData.surface}
            onChange={(val) => handleChange("surface", val)}
          />
          {errors.surface && (
            <p className="text-red-500 text-xs">{errors.surface}</p>
          )}
        </div>

        {/* Hall */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Hall or Assembly Area{" "}
            <span className="text-red-500 text-xs"> * </span>
          </label>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-1">
              <input disabled={isDqaApproved || isDqaRejected}
                type="radio"
                name="hall"
                value="Yes"
                checked={formData.hall === "Yes"}
                onChange={() => handleChange("hall", "Yes")}
                className="accent-blue-600"
              />
              <span className="text-sm text-gray-700">Yes</span>
            </label>
            <label className="flex items-center gap-1">
              <input disabled={isDqaApproved || isDqaRejected}
                type="radio"
                name="hall"
                value="No"
                checked={formData.hall === "No"}
                onChange={() => handleChange("hall", "No")}
                className="accent-blue-600"
              />
              <span className="text-sm text-gray-700">No</span>
            </label>
          </div>
          {errors.hall && <p className="text-red-500 text-xs">{errors.hall}</p>}
        </div>

        {/* Supplementary */}
        <div>
          <ThinDropdownWithSearch disabled={isDqaApproved || isDqaRejected}
            label="Supplementary Learning Materials "
            options={supplementaryOptions}
            value={formData.supplementary}
            onChange={(val) => handleChange("supplementary", val)}
          />
          {errors.supplementary && (
            <p className="text-red-500 text-xs">{errors.supplementary}</p>
          )}
        </div>
        <div>
          {formData.supplementary === "Others" && (
            <div className="">
              <ThinTextField disabled={isDqaApproved || isDqaRejected}
                label="Supplementary Learning Materials (Others)"
                value={formData.learning_materials_others}
                onChange={(e) =>
                  handleChange("learning_materials_others", e.target.value)
                }
              />
              {errors.learning_materials_others && (
                <p className="text-red-500 text-xs">
                  {errors.learning_materials_others}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Extra Curricular Activities */}
        {/* Extra Curricular Activities Yes/No */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Extra-curricular Activities {!isDqaApproved && !isDqaRejected && <span className="text-red-500">*</span>}
          </label>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-1">
              <input disabled={isDqaApproved || isDqaRejected}
                type="radio"
                name="extracurricular_activities"
                value="Yes"
                checked={formData.extracurricular_activities === "Yes"}
                onChange={() =>
                  handleChange("extracurricular_activities", "Yes")
                }
                className="accent-blue-600"
              />
              <span className="text-sm text-gray-700">Yes</span>
            </label>
            <label className="flex items-center gap-1">
              <input disabled={isDqaApproved || isDqaRejected}
                type="radio"
                name="extracurricular_activities"
                value="No"
                checked={formData.extracurricular_activities === "No"}
                onChange={() =>
                  handleChange("extracurricular_activities", "No")
                }
                className="accent-blue-600"
              />
              <span className="text-sm text-gray-700">No</span>
            </label>
          </div>
          {errors.extracurricular_activities && (
            <p className="text-red-500 text-xs">
              {errors.extracurricular_activities}
            </p>
          )}
        </div>

        {/* Extra Curricular Activities Held */}
        {formData.extracurricular_activities === "Yes" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Extra-curricular Activities Held{" "}
              {!isDqaApproved && !isDqaRejected && <span className="text-red-500">*</span>}
            </label>
            <Select disabled={isDqaApproved || isDqaRejected}
              isMulti
              options={extracurricularOptions}
              value={extracurricularOptions.filter((o) =>
                formData.extracurricular?.includes(o.value)
              )}
              onChange={(selected) =>
                handleChange(
                  "extracurricular",
                  selected ? selected.map((s) => s.value) : []
                )
              }
              placeholder="Select activities..."
            />
            {errors.extracurricular && (
              <p className="text-red-500 text-xs">{errors.extracurricular}</p>
            )}
          </div>
        )}
      </div>

      {!isDqaApproved && !isDqaRejected ? (
        <div className="mt-8 flex justify-end">
          <button
            type="button"
            onClick={handleUpdate}
            disabled={isUpdating}
            className={`${
              isUpdating
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-teal-600 hover:bg-teal-700"
            } text-white px-4 py-2 rounded-lg transition duration-200 font-medium`}
          >
            {isUpdating ? "Updating..." : "Update"}
          </button>
        </div>
      ) : (
        <div className=""></div>
      )}
    </>
  );
};

export default LearningFacilities;
